import { CSPaginationData } from "types";
import { buildPaginationRules } from "utils";
import { useState } from "react";

type UsePagerInitialState = Pick<CSPaginationData, "page" | "size">;

const emptyPagerData: CSPaginationData = {
  page: 0,
  size: 0,
  total: 0,
  pageTotal: 0,
};

const buildPaginationTotals = (pageData: CSPaginationData | null) => {
  const rules = pageData?.size ? buildPaginationRules(pageData) : null;
  return rules && pageData ? { from: rules.startIndex + 1, to: rules.endIndex + 1, size: pageData.total } : null;
};

/**
 * Helper to determine if the pager data has been fully populated - all fields are 0 or more in size.
 */
const isFullyPopulatedPageData = (pagerData: CSPaginationData): boolean =>
  Object.values(pagerData).every((x) => x >= 0);

/**
 * Hook to store pager data in react component state.
 * While `page` and `size` are typically stored in the query string (as part of search params),
 * the `pageTotal` and `total` come from the response of a query.
 *
 * Component state is used because if a query takes a while to respond,
 * we ideally like to keep `pageTotal` and `total` available.
 * Storing them in state allows us to do that.
 *
 * Note: We could explore using react-query's `keepPreviousData` to solve this as well.
 * That may end up being a cleaner solution than state:
 * https://react-query-v3.tanstack.com/guides/paginated-queries
 */
const usePager = (initialState: UsePagerInitialState) => {
  const [pageData, setPageData] = useState<CSPaginationData>({ ...emptyPagerData, ...initialState });

  const data = isFullyPopulatedPageData(pageData) ? pageData : null;

  return {
    data,
    paginationTotals: buildPaginationTotals(data),
    set: ({ page, size, pageTotal, total }: CSPaginationData) => setPageData({ page, size, pageTotal, total }),
    clear: () => setPageData(emptyPagerData),
    update: (pagerData: Partial<CSPaginationData>) => setPageData((x) => ({ ...x, ...pagerData })),
  };
};

export default usePager;
